import { Edit03, Eye, File05, Trash02 } from "@untitledui/icons";
import type React from "react";
import { useMemo } from "react";
import { UnboxTable } from "@/components/application/table/unbox-table";
import type { UnboxTableColumn, UnboxTableProps } from "@/components/application/table/unbox-table.types";
import { Button } from "@/components/base/buttons/button";
import { formatDate } from "@/utils/date-conversion";
import type { SmartBriefRow } from "./SmartBriefsListPage";

export interface SmartBriefsTableProps
  extends Omit<
    UnboxTableProps<SmartBriefRow>,
    | "columns"
    | "rowId"
    | "hasActiveFilters"
    | "emptyState"
    | "filteredEmptyState"
    | "size"
    | "rounded"
    | "className"
    | "aria-label"
  > {
  onNavigateToLanding?: () => void;
  onDeleteBrief: (briefId: string) => void;
  onEditBrief: (briefId: string) => void;
}

export function SmartBriefsTable({
  rows,
  onClickRow,
  sorting,
  pagination,
  onNavigateToLanding,
  onDeleteBrief,
  onEditBrief,
}: SmartBriefsTableProps) {
  const columns = useMemo<UnboxTableColumn<SmartBriefRow>[]>(
    () => [
      {
        id: "title",
        label: "Brief Title",
        sortable: true,
        minWidth: "200px",
        render: (_value, row) => (
          <span className="text-xs font-medium text-gray-700 whitespace-nowrap" title={row.title || ""}>
            {row.title}
          </span>
        ),
      },
      {
        id: "createdAt",
        label: "Created",
        sortable: true,
        minWidth: "140px",
        render: (_value, row) => (
          <span className="whitespace-nowrap text-xs text-primary font-medium">{formatDate(row.createdAt)}</span>
        ),
      },
      {
        id: "productName",
        label: "Product",
        sortable: true,
        minWidth: "140px",
        render: (_value, row) => (
          <span className="text-xs font-medium text-gray-700 whitespace-nowrap" title={row.productName || ""}>
            {row.productName}
          </span>
        ),
      },
      {
        id: "updatedAt",
        label: "Last Updated",
        sortable: true,
        minWidth: "140px",
        render: (_value, row) => (
          <span className="whitespace-nowrap text-xs text-primary font-medium">{formatDate(row.updatedAt)}</span>
        ),
      },
      {
        id: "views",
        label: "Views",
        sortable: true,
        minWidth: "100px",
        render: (_value, row) =>
          row.isPublic ? (
            <div className="flex items-center gap-1.5 whitespace-nowrap">
              <Eye className="w-3.5 h-3.5 text-gray-500" />
              <span className="text-xs font-medium text-gray-700">{row.viewCount.toLocaleString()}</span>
            </div>
          ) : (
            <span className="text-xs text-gray-400">-</span>
          ),
      },
      {
        id: "actions",
        label: "",
        minWidth: "109px",
        render: (_value, row) => (
          <div className="flex items-center gap-1">
            <Button
              color="tertiary"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onDeleteBrief(row.id);
              }}
            >
              <Trash02 className="w-4 h-4 text-gray-400 hover:text-red-600" />
            </Button>
            <Button
              color="tertiary"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onEditBrief(row.id);
              }}
            >
              <Edit03 className="w-4 h-4 text-gray-400 hover:text-gray-600" />
            </Button>
          </div>
        ),
      },
    ],
    [onDeleteBrief, onEditBrief],
  );

  return (
    <UnboxTable<SmartBriefRow>
      aria-label="Smart Briefs"
      columns={columns}
      rows={rows}
      rowId="id"
      onClickRow={onClickRow}
      sorting={sorting}
      pagination={pagination}
      className="max-h-[60vh]"
      emptyState={{
        icon: File05,
        title: "No smart briefs created yet",
        action: onNavigateToLanding && { label: "Create your first brief →", onClick: onNavigateToLanding },
      }}
    />
  );
}
